import { useMemo, useState } from 'react'
import { Box } from '@mui/material'
import { TLFilterSelect } from './SchedulerFilterSelect'
import { FONT_SANS, TOKENS } from './SchedulerDesign'
import {
  SchedulerPackagingRoutesRowType,
  SchedulerResourceAndMaterialConstraintsRowType,
  SchedulerResourceSwitchingRowType,
  SchedulerTabsProps,
} from './Scheduler.types'
import { useDeclaredTanks, withoutTanks } from './tankRoutes'
import {
  useScheduleProductionRequirements,
  useSchedulerTableHandlers,
} from './Scheduler.data'
import { SchedulerResourceSwitchingTable } from './SchedulerResourceSwitching.muiTable'

/**
 * Resource Switching — equipment-change times between lines that share one
 * physical set of equipment.
 *
 * Week-invariant: it describes the plant, not the week, so it is fetched and
 * saved at the version root with no week parameter, like Packaging Routes and
 * Cleaning Mapping.
 */
export const ScheduleResourceSwitching = ({
  schedulerId,
  iteration,
}: SchedulerTabsProps) => {
  const API = 'resource-switching'
  const { data: responseData = { data: [], predicate: undefined }, isLoading } =
    useScheduleProductionRequirements(schedulerId, API, iteration)
  const { data: serverRows = [], predicate } = responseData

  const {
    updateTableData,
    uploadScheduleData,
    deleteTableData,
    isSaving,
    isUploading,
    isDeleting,
    updateRowsStatus,
  } = useSchedulerTableHandlers(schedulerId, API, iteration)

  // Packing routes are the only table that lists which lines the plant
  // actually has, so both pickers offer real resources rather than free text
  // the engine would reject as an unknown workstation.
  const { data: routesResponse } = useScheduleProductionRequirements(
    schedulerId,
    'packing-routes',
    iteration
  )
  // A declared tank in a line cell is a vessel, not a line (tankRoutes.ts).
  const declaredTanks = useDeclaredTanks(schedulerId, iteration)
  const lineOptions = useMemo(() => {
    const rows = withoutTanks(
      (routesResponse?.data ?? []) as SchedulerPackagingRoutesRowType[],
      declaredTanks
    )
    const lines = new Set<string>()
    for (const r of rows) {
      for (const line of [
        r?.packingLine1,
        r?.packingLine2,
        r?.packingLine3,
        r?.packingLine4,
      ]) {
        if (line) lines.add(line)
      }
    }
    return Array.from(lines).sort()
  }, [routesResponse, declaredTanks])

  // ── Filters ───────────────────────────────────────────────────────────
  // The scaffold is every ordered pair, so on a 22-line plant it is 462 rows
  // and finding one transition by eye is impractical. `resourceFilter` matches
  // EITHER side of the pair, because a planner looking for a line wants both
  // the moves onto it and the moves off it; `onlyConfigured` is the shortcut
  // for "what has anyone actually set?", which is the common question once the
  // table has been filled in and is otherwise a needle in 462 zeros.
  // Multi-select, matching the factory schedule's own filter row (same
  // `TLFilterSelect` control) rather than a second differently-behaved one.
  // From and To are separate so a single pair can be pinned exactly; leaving
  // one empty means "any".
  const [fromFilter, setFromFilter] = useState<string[]>([])
  const [toFilter, setToFilter] = useState<string[]>([])
  const [onlyConfigured, setOnlyConfigured] = useState(false)

  // Every ordered pair of lines, at 0, with anything saved overlaid on top.
  //
  // The stored table only ever holds the handful of pairs that actually carry
  // a time — saving sends changed rows only — but a planner cannot fill in a
  // grid that starts empty, and cannot discover which pairs exist. So the full
  // matrix is scaffolded client-side and the saved rows replace their slots.
  //
  // This is safe *because* 0 is inert in the engine: a scaffolded pair forms no
  // group and imposes no exclusion. If 0 meant "shares equipment", this default
  // would make every line in the plant mutually exclusive.
  //
  // Synthetic ids are negative and derived from the pair's position, so they
  // are stable across renders and cannot collide with a server row's id.
  const rows = useMemo(() => {
    const saved = serverRows as SchedulerResourceSwitchingRowType[]
    const bySlot = new Map<string, SchedulerResourceSwitchingRowType>()
    for (const r of saved) {
      bySlot.set(`${r.previous_resource}->${r.next_resource}`, r)
    }
    const out: SchedulerResourceSwitchingRowType[] = []
    let slot = 0
    for (const from of lineOptions) {
      for (const to of lineOptions) {
        if (from === to) continue
        slot += 1
        const existing = bySlot.get(`${from}->${to}`)
        out.push(
          existing ?? {
            id: -slot,
            previous_resource: from,
            next_resource: to,
            switching_time: '0',
          }
        )
        bySlot.delete(`${from}->${to}`)
      }
    }
    // Anything saved against a line the packaging routes no longer list still
    // has to be visible — it is live data the engine will read, and hiding it
    // would make a stale row impossible to find or clear.
    for (const leftover of bySlot.values()) out.push(leftover)
    return out
  }, [serverRows, lineOptions])

  // Resource constraints, for the Resource Constraint column: a switch can
  // consume a resource while the equipment moves, which is how changing one
  // line over takes down anything else that needs the same resource.
  const { data: constraintsResponse } = useScheduleProductionRequirements(
    schedulerId,
    'resource-and-material-constraints',
    iteration
  )
  const resourceOptions = useMemo(() => {
    const rows = (constraintsResponse?.data ??
      []) as SchedulerResourceAndMaterialConstraintsRowType[]
    const names = new Set<string>()
    for (const r of rows) {
      if (r?.ResourceConstraint) names.add(String(r.ResourceConstraint))
    }
    return Array.from(names).sort()
  }, [constraintsResponse])

  const filterOptions = useMemo(
    () => lineOptions.map((line) => ({ value: line, label: line })),
    [lineOptions]
  )

  const visibleRows = useMemo(() => {
    return rows.filter((r) => {
      if (fromFilter.length && !fromFilter.includes(r.previous_resource)) {
        return false
      }
      if (toFilter.length && !toFilter.includes(r.next_resource)) return false
      if (onlyConfigured && !(Number(r.switching_time) > 0)) return false
      return true
    })
  }, [rows, fromFilter, toFilter, onlyConfigured])

  const configuredCount = useMemo(
    () => rows.filter((r) => Number(r.switching_time) > 0).length,
    [rows]
  )

  return (
    <Box sx={{ height: 'calc(100vh - 240px)', minHeight: 400 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          mb: 1.5,
          flexWrap: 'wrap',
        }}
      >
        <TLFilterSelect
          label="From"
          value={fromFilter}
          onChange={setFromFilter}
          options={filterOptions}
        />
        <TLFilterSelect
          label="To"
          value={toFilter}
          onChange={setToFilter}
          options={filterOptions}
        />
        <Box
          component="button"
          onClick={() => setOnlyConfigured((v) => !v)}
          sx={{
            height: 24,
            px: '9px',
            border: `1px solid ${
              onlyConfigured ? TOKENS.stateComplete : TOKENS.line300
            }`,
            borderRadius: '2px',
            bgcolor: onlyConfigured ? 'rgba(14,124,134,0.08)' : '#fff',
            color: onlyConfigured ? TOKENS.stateComplete : TOKENS.ink500,
            fontFamily: FONT_SANS,
            fontSize: 10.5,
            fontWeight: 600,
            cursor: 'pointer',
            whiteSpace: 'nowrap',
          }}
        >
          Only configured
        </Box>
        <Box
          sx={{
            fontFamily: FONT_SANS,
            fontSize: 10.5,
            color: TOKENS.ink400,
            whiteSpace: 'nowrap',
          }}
        >
          {visibleRows.length} of {rows.length} shown · {configuredCount}{' '}
          configured
        </Box>
      </Box>
      <SchedulerResourceSwitchingTable
        data={visibleRows}
        isLoading={isLoading}
        updateTableData={updateTableData}
        uploadScheduleData={uploadScheduleData}
        isUploading={isUploading}
        isSaving={isSaving}
        updateRowsStatus={updateRowsStatus}
        predicate={predicate}
        deleteTableData={deleteTableData}
        isDeleting={isDeleting}
        lineOptions={lineOptions}
        resourceOptions={resourceOptions}
      />
    </Box>
  )
}
